import { queryClient, useGet, usePost } from '@lovoldsystem/core.client'
import { axios } from 'src/axios'
import { UserStatus } from 'src/enums/user-status'
import { UserRegistrationFormType, UserUpdateFormType } from '../forms/UserRegistrationForm'
import { IUser, IUserWithPortalAccess } from '../generated-types'
import { IApiResult } from '../generated-types/api-result'
import { userQueryKey } from '../query-keys'

const baseUrl = () => 'users'

export const userEndpoints = {
  getUserInvite(code: string) {
    return `${baseUrl()}/invites/${code}`
  },
  sendSystemInvite() {
    return `${baseUrl()}/system/invite`
  },
  getUserInviteURL() {
    return `${baseUrl()}/system/invite`
  },
  registerUer() {
    return `${baseUrl()}/register`
  },
  updateUser() {
    return `${baseUrl()}/update-me`
  },
  allUsers() {
    return `${baseUrl()}/paginated`
  },
  me: () => `${baseUrl()}/me`,
  renewBulkUserInvite() {
    return `${baseUrl()}/system/invite/bulk`
  },
  getSystemProjectAdmins() {
    return `${baseUrl()}/system-project-admins`
  },
  assignSystemProjectAdmin() {
    return `${baseUrl()}/add-project-admin`
  },
  addProjectUser() {
    return `${baseUrl()}/add-project-user`
  },
}

export const invalidateSystemUserCache = {
  getUserInvite(code: string) {
    return queryClient.invalidateQueries(userQueryKey.GET_USER_INVITE(code))
  },
  getSystemUsers() {
    return queryClient.invalidateQueries(userQueryKey.GET_SYSTEM_USERS())
  },
  getSystemProjectAdmins() {
    return queryClient.invalidateQueries(userEndpoints.getSystemProjectAdmins())
  },
}

export const useGetUserInvite = (code: string) =>
  useGet<any>(userEndpoints.getUserInvite(code), undefined, { queryKey: userQueryKey.GET_USER_INVITE(code) })

export const useCreateSystemInvite = () => usePost<IApiResult, { email: string }>(userEndpoints.sendSystemInvite())
export const useGetUserInviteURL = (email: string) => useGet<string>(userEndpoints.getUserInviteURL(), { email })

export const useBulkUserInvite = () => usePost<IApiResult, { emails: string[] }>(userEndpoints.renewBulkUserInvite())

export const useRegisterUser = () => usePost<IApiResult, UserRegistrationFormType>(userEndpoints.registerUer())

export const useUpdateUser = () => usePost<IApiResult, UserUpdateFormType>(userEndpoints.updateUser())

export const useAllUsers = (
  searchText: string | undefined,
  status: UserStatus | undefined,
  page = 0,
  pageSize = 1000,
) =>
  useGet<IUser[]>(
    userEndpoints.allUsers(),
    { page, pageSize, searchText, status },
    { queryKey: userQueryKey.GET_SYSTEM_USERS(searchText, status) },
  )

export const useGetMe = () => {
  const result = useGet<IUserWithPortalAccess>(userEndpoints.me(), {}, { cacheTime: 0, retry: 3 })
  axios.defaults.headers['X-User'] = result.data?.encryptedUserId

  return result
}

export const useGetSystemProjectAdmins = () => useGet<IUserWithPortalAccess[]>(userEndpoints.getSystemProjectAdmins())

export const useAssignSystemProjectAdmin = () =>
  usePost<IApiResult, { email: string }>(userEndpoints.assignSystemProjectAdmin())

export const useAddProjectUser = () => usePost<IUser, Omit<IUser, 'id' | 'status'>>(userEndpoints.addProjectUser())
